import React, { useEffect, useState } from 'react';
import { Plus, Loader2, Download, FileUp } from 'lucide-react';
import * as XLSX from 'xlsx';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../../firebase';
//@ts-ignore
import HRContactCard from './HRContactCard';
//@ts-ignore
import AddOrEditHRContact from './AddOrEditHRContact';
//@ts-ignore
import ConfirmDelete from './ConfirmDelete';

const HRContacts = ({setLoader,addToast}) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [deleteContact, setDeleteContact] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const fetchContacts = async () => {
    setLoading(true);
    const querySnapshot = await getDocs(collection(db, 'hrContacts'));
    const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setContacts(data);
    setLoading(false);
  };

  // Export to Excel
  const handleExportExcel = () => {
    const exportData = contacts.map(({ id, ...rest }) => rest);
    const ws = XLSX.utils.json_to_sheet(exportData, { header: [
      'fullName', 'mobileNo', 'companyName', 'emailId', 'verified', 'note'
    ] });
    // Bold headers
    const header = ['Full Name', 'Mobile No', 'Company Name', 'Email ID', 'Verified', 'Note'];
    XLSX.utils.sheet_add_aoa(ws, [header], { origin: 'A1' });
    // Style header row bold
    ws['!rows'] = [{ hpt: 24 }];
    ws['A1'].s = ws['B1'].s = ws['C1'].s = ws['D1'].s = ws['E1'].s = ws['F1'].s = { font: { bold: true } };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'HR Contacts');
    XLSX.writeFile(wb, 'hr_contacts.xlsx');
  };

  // Import from Excel
  const handleImportExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
      // Add each contact to Firestore
      for (const _row of data) {
        const row = _row as Record<string, any>;
        // Only add if required fields are present
        if (row.fullName && row.mobileNo && row.companyName) {
          await addDoc(collection(db, 'hrContacts'), {
            fullName: row.fullName,
            mobileNo: row.mobileNo,
            companyName: row.companyName,
            emailId: row.emailId || '',
            verified: !!row.verified,
            note: row.note || '',
          });
        }
      }
      fetchContacts();
    };
    reader.readAsBinaryString(file);
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleAdd = () => {
    setEditContact(null);
    setShowModal(true);
  };

  const handleEdit = (contact) => {
    setEditContact(contact);
    setShowModal(true);
  };

  const handleDelete = (contact) => {
    setDeleteContact(contact);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteContact) {
      await deleteDoc(doc(db, 'hrContacts', deleteContact.id));
      setShowDeleteConfirm(false);
      setDeleteContact(null);
      fetchContacts();
    }
  };

  const handleModalClose = (refresh = false) => {
    setShowModal(false);
    setEditContact(null);
    if (refresh) fetchContacts();
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 relative">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-3">
        <h2 className="text-2xl font-bold text-gray-900">HR Contacts</h2>
        <div className="flex gap-2 w-full sm:w-auto justify-end">
          <button
            className="flex items-center justify-center w-10 h-10 bg-[#0071bd] text-white rounded-full shadow hover:bg-[#005fa3] transition-colors"
            onClick={handleAdd}
            title="Add"
          >
            <Plus className="w-5 h-5" />
          </button>
          <button
            className="flex items-center justify-center w-10 h-10 bg-green-600 text-white rounded-full shadow hover:bg-green-700 transition-colors"
            onClick={handleExportExcel}
            title="Export to Excel"
          >
            <Download className="w-5 h-5" />
          </button>
          <label className="flex items-center justify-center w-10 h-10 bg-yellow-500 text-white rounded-full shadow hover:bg-yellow-600 transition-colors cursor-pointer" title="Import from Excel">
            <FileUp className="w-5 h-5" />
            <input type="file" accept=".xlsx,.xls" onChange={handleImportExcel} className="hidden" />
          </label>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center items-center h-32">
          <Loader2 className="animate-spin w-8 h-8 text-[#0071bd]" />
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center text-gray-500">No HR contacts found.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {contacts.map(contact => (
            <HRContactCard
              key={contact.id}
              contact={contact}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
      {showModal && (
        <AddOrEditHRContact
          contact={editContact}
          onClose={handleModalClose}
        />
      )}
      {showDeleteConfirm && (
        <ConfirmDelete
          open={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete HR Contact"
          message={`Are you sure you want to delete ${deleteContact?.fullName}?`}
        />
      )}
    </div>
  );
};

export default HRContacts;
